import { RuleObject } from 'antd/lib/form';
import moment from 'moment';
import { TRegexPatters, TValidationRules } from './validationRules.type';

export const regexPatterns: TRegexPatters = {
  oneUpperCase: /[A-Z]+/,
  oneLowerCase: /[a-z]+/,
  oneNumber: /\d/,
  oneSymbol: /[*@!#%&$_+=?.<>;:'"/()^~{}]+/,
  letters8: /.{8,}/,
  phoneNumber: /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
  domainName: /^((?!-)[A-Za-z0-9-]{1,63}(?!-)\.)+[A-Za-z]{2,63}$/,
  url: /[(http(s)?):(www)?a-zA-Z0-9@:%._~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_.~#?&//=]*)/,
  onlyWhiteSpace: /.*[^ ].*/,
  subDomain: /^[a-z][a-z0-9.-]+[a-z0-9]$/,
  emailsSeparatedWithComma: /^([\w+-.%]+@[\w-.]+\.[A-Za-z]{2,}(\s*,?\s*)*)+$/,
  onlyEnglishLetters: /^[a-zA-Z\s]*$/,
};

export const passwordValidationRegex: RuleObject[] = [
  { type: 'regexp', pattern: regexPatterns.oneUpperCase },
  { type: 'regexp', pattern: regexPatterns.oneLowerCase },
  { type: 'regexp', pattern: regexPatterns.oneNumber },
  { type: 'regexp', pattern: regexPatterns.oneSymbol },
  { type: 'regexp', pattern: regexPatterns.letters8 },
];

export const passwordValidator = (
  value: string,
  rulesCount: number,
): boolean => {
  let count = 0;
  passwordValidationRegex.forEach((rule: RuleObject) => {
    if (rule.pattern?.test(value)) {
      count += 1;
    }
  });
  if (count >= rulesCount) {
    return true;
  }
  return false;
};

export const validationRules: TValidationRules = {
  required: fieldName => ({
    // Kept so antd applies .ant-form-item-required (needed for the asterisk
    // CSS). async-validator runs the custom `validator` below instead of
    // its built-in required check, so this rule emits a single error
    // message. Additional rules in the same `rules` array still run
    // independently and may surface their own errors.
    required: true,
    validator(_: RuleObject, value: any) {
      // Strings: non-empty after trim.
      // Arrays: must have at least one item (multi-select, checkbox group).
      // Other non-strings (Moment, objects, custom components): non-null/
      // non-undefined. Treat 0 and false as valid — they're legitimate values
      // for InputNumber (discount=0) and Checkbox (opt-out=false).
      if (
        (typeof value === 'string' && value.trim()) ||
        (Array.isArray(value) && value.length > 0) ||
        (typeof value !== 'string' &&
          !Array.isArray(value) &&
          value !== null &&
          value !== undefined)
      ) {
        return Promise.resolve();
      }
      return Promise.reject(
        new Error(
          fieldName
            ? `The ${fieldName} is required!`
            : 'This field is required!',
        ),
      );
    },
  }),
  email: () => ({
    type: 'email',
    message: 'Incorrect email address!',
  }),
  multipleEmails: () => ({
    pattern: regexPatterns.emailsSeparatedWithComma,
    message: 'Incorrect email address!',
  }),
  phoneNumber: () => ({
    pattern: regexPatterns.phoneNumber,
    message: 'Invalid phone number format',
  }),
  password: [
    {
      validator(_: RuleObject, value: any) {
        if (passwordValidator(value, 4)) {
          return Promise.resolve();
        }
        return Promise.reject(new Error(''));
      },
    },
  ],
  acceptTerms: [
    {
      validator(_: RuleObject, value: any) {
        if (value) {
          return Promise.resolve();
        }
        return Promise.reject(new Error('Please accept Terms and Conditions!'));
      },
    },
  ],
  passwordConfirm: [
    ({ getFieldValue }) => ({
      validator(_: RuleObject, value: any) {
        if (getFieldValue('password') === value) {
          return Promise.resolve();
        }
        return Promise.reject(new Error('The passwords do not match!'));
      },
    }),
  ],
  startTime: [
    ({ getFieldValue, setFields }) => ({
      validator(_: RuleObject, value: string) {
        if (!value) {
          return Promise.reject(new Error('The field is required!'));
        }
        if (moment(getFieldValue('endTime')).isSame(moment(value), 'minutes')) {
          return Promise.reject(
            new Error('Start time should not be same as end time'),
          );
        }
        if (
          !getFieldValue('endTime') ||
          moment(getFieldValue('endTime')) > moment(value)
        ) {
          setFields([
            {
              name: 'endTime',
              errors: [],
            },
          ]);
          return Promise.resolve();
        }
        return Promise.reject(
          new Error('The start time must be earlier than end'),
        );
      },
    }),
  ],
  endTime: [
    ({ getFieldValue, setFields }) => ({
      validator(_: RuleObject, value: string) {
        if (!value) {
          return Promise.reject(new Error('The field is required!'));
        }
        if (
          moment(getFieldValue('startTime')).isSame(moment(value), 'minutes')
        ) {
          return Promise.reject(
            new Error('End time should not be same as start time'),
          );
        }
        if (
          !getFieldValue('startTime') ||
          moment(getFieldValue('startTime')) < moment(value)
        ) {
          setFields([
            {
              name: 'startTime',
              errors: [],
            },
          ]);
          return Promise.resolve();
        }
        return Promise.reject(
          new Error('The end time must be later than start'),
        );
      },
    }),
  ],
  startDate: [
    ({ getFieldValue, setFields }) => ({
      validator(_: RuleObject, value: string) {
        if (!value) {
          return Promise.reject(new Error('The field is required!'));
        }
        if (
          !getFieldValue('endDate') ||
          moment(getFieldValue('endDate')) > moment(value)
        ) {
          setFields([
            {
              name: 'endDate',
              errors: [],
            },
          ]);
          return Promise.resolve();
        }
        return Promise.reject(
          new Error('The start date must be earlier than end'),
        );
      },
    }),
  ],
  endDate: [
    ({ getFieldValue, setFields }) => ({
      validator(_: RuleObject, value: string) {
        if (!value) {
          return Promise.reject(new Error('The field is required!'));
        }
        if (
          !getFieldValue('startDate') ||
          moment(getFieldValue('startDate')) < moment(value)
        ) {
          setFields([
            {
              name: 'startDate',
              errors: [],
            },
          ]);
          return Promise.resolve();
        }
        return Promise.reject(
          new Error('The end date must be later than start'),
        );
      },
    }),
  ],
  endDateOptional: [
    ({ getFieldValue, setFields }) => ({
      validator(_: RuleObject, value: string) {
        if (!value) {
          return Promise.resolve();
        }
        if (
          !getFieldValue('startDate') ||
          moment(getFieldValue('startDate')) < moment(value)
        ) {
          setFields([
            {
              name: 'startDate',
              errors: [],
            },
          ]);
          return Promise.resolve();
        }
        return Promise.reject(
          new Error('The end date must be later than start'),
        );
      },
    }),
  ],
  startTimeOptional: [
    ({ getFieldValue, setFields }) => ({
      validator(_: RuleObject, value: string) {
        if (
          !getFieldValue('endTime') ||
          moment(getFieldValue('endTime')) > moment(value)
        ) {
          setFields([
            {
              name: 'endTime',
              errors: [],
            },
          ]);
          return Promise.resolve();
        }
        return Promise.reject(
          new Error('The start time must be earlier than end'),
        );
      },
    }),
  ],
  endTimeOptional: [
    ({ getFieldValue, setFields }) => ({
      validator(_: RuleObject, value: string) {
        if (
          !getFieldValue('startTime') ||
          moment(getFieldValue('startTime')) < moment(value)
        ) {
          setFields([
            {
              name: 'startTime',
              errors: [],
            },
          ]);
          return Promise.resolve();
        }
        return Promise.reject(
          new Error('The end time must be later than start'),
        );
      },
    }),
  ],
  integerMoreThanZeroOptional: () => ({
    validator(_: RuleObject, value: any) {
      if (value > 0 || !value) {
        return Promise.resolve();
      }
      return Promise.reject(new Error('The value should be greater than 0'));
    },
  }),
  integerFromZeroToHundred: () => ({
    validator(_: RuleObject, value: any) {
      if (value >= 0 && value <= 100) {
        return Promise.resolve();
      }
      return Promise.reject(new Error('The value should be between 0 and 100'));
    },
  }),
  integerWithDecimalsRequired: () => ({
    // Kept so antd applies .ant-form-item-required (needed for the asterisk
    // CSS). async-validator runs the custom `validator` below instead of
    // its built-in required check, so this rule emits a single error
    // message. Additional rules in the same `rules` array still run
    // independently and may surface their own errors.
    required: true,
    validator(_: RuleObject, value: any) {
      // Empty check: reject only null/undefined/empty string.
      // 0 is a legitimate value (e.g. a free service item with cost=0).
      if (value === null || value === undefined || value === '') {
        return Promise.reject(new Error('This field is required'));
      }
      if (Number.isNaN(+value)) {
        return Promise.reject(new Error('The value should be positive number'));
      }
      if (!String(value).match(/^[+-](\d*\.)?\d+$/g)) {
        return Promise.resolve();
      }
      return Promise.reject(new Error('The value should be positive number'));
    },
  }),
  integerWithDecimalsOptional: () => ({
    validator(_: RuleObject, value: any) {
      if (!value.match(/^[+-](\d*\.)?\d+$/g)) {
        return Promise.resolve();
      }
      return Promise.reject(new Error('The value should be positive number'));
    },
  }),

  integerOptional: () => ({
    validator(_: RuleObject, value: any) {
      if (!value.match(/[^\d]/g)) {
        return Promise.resolve();
      }
      return Promise.reject(new Error('The value should be positive number'));
    },
  }),
  onePointDecimal: () => ({
    validator(_: RuleObject, value: any) {
      if (!!value && !!value.split('.')[1]) {
        if (value.split('.')[1].length > 1) {
          return Promise.reject(
            new Error('The value should contain one decimal'),
          );
        }
      }
      return Promise.resolve();
    },
  }),
  domainNameOptional: () => ({
    validator(_: RuleObject, value: string[]) {
      if (
        !value ||
        !value.length ||
        value.every(item => item.match(regexPatterns.domainName))
      ) {
        return Promise.resolve();
      }
      return Promise.reject(new Error('Invalid domain name'));
    },
  }),
  onlyEnglishLetters: () => ({
    pattern: regexPatterns.onlyEnglishLetters,
    message: 'Only english letters are allowed',
  }),
};
